import { z } from 'zod';

import { CATEGORY_KINDS, TRANSACTION_DIRECTIONS, WALLET_TYPES } from '../enums';
import {
  zColor,
  zCurrency,
  zISODate,
  zId,
  zMoney,
  zNonEmptyString,
  zOptionalString,
  zPagination,
  zSignedMinorAmount,
  zSort,
} from './common';

/* ─── Кошельки ──────────────────────────────────────────────────────────── */

export const createWalletSchema = z.object({
  name: zNonEmptyString(60),
  type: z.enum(WALLET_TYPES).default('BANK'),
  currency: zCurrency,
  openingBalanceMinor: zSignedMinorAmount.default(0),
  color: zColor.default('#6366F1'),
  icon: z.string().max(40).default('wallet'),
  includeInTotals: z.boolean().default(true),
  /** Адрес кошелька — только для типа CRYPTO. */
  cryptoAddress: zOptionalString(120),
  notes: zOptionalString(500),
});
export type CreateWalletInput = z.infer<typeof createWalletSchema>;

export const updateWalletSchema = createWalletSchema
  .partial()
  .omit({ currency: true })
  .extend({
    isArchived: z.boolean().optional(),
    sortOrder: z.number().int().min(0).max(999).optional(),
  });
export type UpdateWalletInput = z.infer<typeof updateWalletSchema>;

/* ─── Категории ─────────────────────────────────────────────────────────── */

export const createCategorySchema = z.object({
  name: zNonEmptyString(60),
  kind: z.enum(CATEGORY_KINDS),
  color: zColor.default('#64748B'),
  icon: z.string().max(40).default('tag'),
});
export type CreateCategoryInput = z.infer<typeof createCategorySchema>;

export const updateCategorySchema = createCategorySchema.partial().extend({
  isArchived: z.boolean().optional(),
  sortOrder: z.number().int().min(0).max(999).optional(),
});
export type UpdateCategoryInput = z.infer<typeof updateCategorySchema>;

export const mergeCategoriesSchema = z.object({
  sourceId: zId,
  targetId: zId,
});

/* ─── Операции ──────────────────────────────────────────────────────────── */

export const createTransactionSchema = z.object({
  walletId: zId,
  direction: z.enum(TRANSACTION_DIRECTIONS),
  /** Сумма всегда положительная; знак задаёт direction. */
  amountMinor: zMoney,
  occurredAt: zISODate,
  categoryId: zId.nullish(),
  personId: zId.nullish(),
  counterparty: zOptionalString(120),
  description: zOptionalString(500),
  /** Расход отнесён на конкретного человека, но это не зарплата (F-04). */
  isPersonCost: z.boolean().default(false),
});
export type CreateTransactionInput = z.infer<typeof createTransactionSchema>;

export const updateTransactionSchema = createTransactionSchema.partial().omit({ walletId: true });
export type UpdateTransactionInput = z.infer<typeof updateTransactionSchema>;

export const createTransferSchema = z
  .object({
    fromWalletId: zId,
    toWalletId: zId,
    amountMinor: zMoney,
    /**
     * Сколько дошло до второго кошелька. Отличается от amountMinor при
     * разных валютах или когда банк удержал комиссию.
     */
    receivedMinor: zMoney.optional(),
    occurredAt: zISODate,
    description: zOptionalString(500),
  })
  .refine((value) => value.fromWalletId !== value.toWalletId, {
    message: 'validation.sameWallet',
    path: ['toWalletId'],
  });
export type CreateTransferInput = z.infer<typeof createTransferSchema>;

export const transactionFilterSchema = zPagination.merge(zSort).extend({
  from: zISODate.optional(),
  to: zISODate.optional(),
  walletId: zId.optional(),
  categoryId: zId.optional(),
  personId: zId.optional(),
  direction: z.enum(TRANSACTION_DIRECTIONS).optional(),
  search: z.string().trim().max(120).optional(),
  /** Показывать ли половинки переводов между своими кошельками. */
  includeTransfers: z.coerce.boolean().default(true),
});
export type TransactionFilter = z.infer<typeof transactionFilterSchema>;

export const bulkTransactionSchema = z.object({
  ids: z.array(zId).min(1).max(200),
  action: z.enum(['setCategory', 'delete']),
  categoryId: zId.nullish(),
});

/* ─── Курсы валют ───────────────────────────────────────────────────────── */

export const setExchangeRateSchema = z.object({
  from: zCurrency,
  to: zCurrency,
  rate: z.number().positive().max(1_000_000),
  validFrom: zISODate,
});
export type SetExchangeRateInput = z.infer<typeof setExchangeRateSchema>;

/* ─── Налоговые пресеты ─────────────────────────────────────────────────── */

export const createTaxPresetSchema = z.object({
  name: zNonEmptyString(40),
  rate: z.number().min(0).max(100),
  mode: z.enum(['ORG_PAYS', 'PERSON_PAYS', 'NO_TAX']).default('PERSON_PAYS'),
  isDefault: z.boolean().default(false),
});
export type CreateTaxPresetInput = z.infer<typeof createTaxPresetSchema>;

/* ─── Бюджеты по категориям (F-07) ──────────────────────────────────────── */

export const createBudgetSchema = z.object({
  categoryId: zId,
  periodType: z.enum(['MONTH', 'QUARTER', 'YEAR']).default('MONTH'),
  amountMinor: zMoney,
  startsAt: zISODate,
  alertAtPercent: z.number().int().min(50).max(100).default(80),
});
export type CreateBudgetInput = z.infer<typeof createBudgetSchema>;

/* ─── Ответы ────────────────────────────────────────────────────────────── */

export interface WalletDto {
  id: string;
  name: string;
  type: string;
  currency: string;
  openingBalanceMinor: number;
  balanceMinor: number;
  /** Пересчитано в базовую валюту воркспейса по последнему курсу. */
  balanceInBaseMinor: number;
  color: string;
  icon: string;
  includeInTotals: boolean;
  cryptoAddress: string | null;
  notes: string | null;
  isArchived: boolean;
  sortOrder: number;
  transactionCount: number;
  /** Сколько уже запланировано к списанию с этого кошелька. */
  plannedOutMinor: number;
  createdAt: string;
}

export interface CategoryDto {
  id: string;
  name: string;
  kind: 'INCOME' | 'EXPENSE';
  color: string;
  icon: string;
  isSystem: boolean;
  isArchived: boolean;
  sortOrder: number;
  transactionCount: number;
}

export interface TransactionDto {
  id: string;
  walletId: string;
  walletName: string;
  walletCurrency: string;
  direction: 'IN' | 'OUT';
  amountMinor: number;
  currency: string;
  amountInBaseMinor: number;
  occurredAt: string;
  categoryId: string | null;
  categoryName: string | null;
  categoryColor: string | null;
  personId: string | null;
  personNickname: string | null;
  counterparty: string | null;
  description: string | null;
  transferGroupId: string | null;
  isPersonCost: boolean;
  isTaxPayment: boolean;
  payoutId: string | null;
  billId: string | null;
  /** Приложенный чек или счёт. Имя нужно, чтобы показать его до скачивания. */
  attachmentName: string | null;
  hasAttachment: boolean;
  createdAt: string;
}

export interface TransactionListResponse {
  items: TransactionDto[];
  total: number;
  page: number;
  pageSize: number;
  pageCount: number;
  /** Итоги по текущему фильтру, в базовой валюте. */
  totals: {
    incomeMinor: number;
    expenseMinor: number;
    netMinor: number;
  };
}

export interface WalletsSummary {
  wallets: WalletDto[];
  totalInBaseMinor: number;
  baseCurrency: string;
  /** Остатки по каждой валюте без пересчёта (F-27). */
  byCurrency: { currency: string; balanceMinor: number; walletCount: number }[];
  /** Валюты, для которых нет курса к базовой — их не пересчитать. */
  missingRates: string[];
}

export interface ExchangeRateDto {
  id: string;
  from: string;
  to: string;
  rate: number;
  validFrom: string;
  source: string;
}
